import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import Sidebar from '../Parts/Sidebar';
import Header from '../Parts/Header';
import Footer from '../Parts/Footer';
import { useLogoTitle } from '../classes/LogoTitleContext';
import {
    ContentContainer,
    MainContent,
    CenteredCard,
    ResetButton,
    ResetIcon,
    LampSVGReset,
} from '../Styles/RemoteAccess';
import getAuthHeaders from '../Auth/Authentication';
import { API_ENDPOINTS } from '../Auth/apiConfig';
import refreshIcon from '../Images/refresh-icon-remote-access.svg';

const RemoteAccessReset = ({ onLogout, loggedInUser, clientId }) => {
    const theme = useTheme();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
    const { logoUrl } = useLogoTitle();
    const [filteredData, setFilteredData] = useState([]);
    const [vacuumLampPump1State, setVacuumLampPump1State] = useState(null);
    const [vacuumLampPump2State, setVacuumLampPump2State] = useState(null);
    const [dischargeLampPump1State, setDischargeLampPump1State] = useState(null);
    const [dischargeLampPump2State, setDischargeLampPump2State] = useState(null);

    const [resetLampState, setResetLampState] = useState('white');
    const [pumpStatusData, setPumpStatusData] = useState({});
    const [fetchDataFlag, setFetchDataFlag] = useState(false);

    // Refresh handler
    const handleRefresh = () => {
        alert('Data refreshed successfully!');
        window.location.reload();
    };

    useEffect(() => {
        const fetchPumpModeStatus = async () => {
            try {
                const authHeaders = getAuthHeaders(); // Ensure you have your auth headers
                const response = await axios.post(
                    `${API_ENDPOINTS.RemoteAccess_VacTankLvl_Reset_Only}`,
                    { clientId: loggedInUser?.clientId }, // ✅ send clientId dynamically
                    { headers: { ...authHeaders } }
                );
                //console.log('Pump Mode Status API Response:', response.data);

                const { data } = response.data;
                if (data && Array.isArray(data)) {
                    // Store the entire fetched data
                    setFilteredData(data);

                    // Process data into a structured object
                    const processedData = data.reduce((acc, item) => {
                        acc[item.TagName] = item.Value;
                        return acc;
                    }, {});

                    //console.log('Processed Pump Status Data:', processedData);

                    // Update pump mode status
                    setPumpStatusData(processedData);

                    // Update lamp states based on VP1, VP2, DP1, DP2
                    setVacuumLampPump1State(processedData.VP1 === '1' ? 'green' : 'red');
                    setVacuumLampPump2State(processedData.VP2 === '1' ? 'green' : 'red');
                    setDischargeLampPump1State(processedData.DP1 === '1' ? 'green' : 'red');
                    setDischargeLampPump2State(processedData.DP2 === '1' ? 'green' : 'red');
                } else {
                    console.error('[ERROR] Invalid data format received from backend.');
                }
            } catch (error) {
                console.error('Error fetching pump status:', error.response ? error.response.data : error.message);
            }
        };

        // Fetch data every 5 seconds
        const intervalId = setInterval(fetchPumpModeStatus, 5000);

        // Cleanup function to clear interval on unmount
        return () => clearInterval(intervalId);
    }, [fetchDataFlag]);

    // Reset handler
    const handleResetClick = async (CommandID, CommandInput, CommandAck) => {
        const confirmReset = window.confirm('Are you sure you want to perform the reset?');
        if (!confirmReset) return;

        try {
            const authHeaders = getAuthHeaders();

            const response = await fetch(`${API_ENDPOINTS.RemoteAccess_Reset_Only}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...authHeaders,
                },
                body: JSON.stringify({
                    CommandID,
                    CommandInput,
                    CommandAck,
                    clientId: loggedInUser?.clientId, // ✅ send clientId dynamically
                }),
            });

            const data = await response.json();

            if (data?.status === 'success' && data?.data?.length > 0) {
                data.data.forEach(({ TagName, Value }) => {
                    if (TagName === 'vLinkResetResult') {
                        if (Value === '1') setResetLampState('green');
                        else if (Value === '0') setResetLampState('red');
                    }
                });
                alert('Command sent successfully.');
            } else {
                alert('Command not sent, please contact support team.');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Command not sent, please contact support team.');
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div style={{ backgroundColor: '#EBEFF3', minHeight: '100vh' }}>
                <Sidebar loggedInUser={loggedInUser} />
                <Header logoUrl={logoUrl} loggedInUser={loggedInUser} onLogout={onLogout} />
                <ContentContainer>
                    <MainContent>
                        {/* Centered Card */}
                        <CenteredCard>
                            {/* Top Section: Levels + Refresh */}
                            <div
                                style={{
                                    position: 'relative',
                                    width: '100%',
                                    marginTop: '10px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: '20px',
                                }}
                            >
                                {/* Left Box with Vacuum and Tank Levels */}
                                <div
                                    style={{
                                        display: 'flex',
                                        backgroundColor: '#f9f9f9',
                                        padding: '6px 6px',
                                        borderRadius: '16px',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                    }}
                                >
                                    {/* Vacuum Level */}
                                    <div
                                        style={{
                                            background: 'linear-gradient(135deg, rgb(189, 231, 250), rgb(147, 227, 253))',
                                            color: 'black',
                                            padding: '6px 8px',
                                            borderRadius: '12px',
                                            marginRight: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                                        }}
                                    >
                                        Vacuum Level | {pumpStatusData['Vaccum_Level'] || '0'}
                                    </div>

                                    {/* Tank Level */}
                                    <div
                                        style={{
                                            background: 'linear-gradient(135deg, rgb(193, 247, 193), rgb(170, 241, 170))',
                                            color: 'black',
                                            padding: '6px 8px',
                                            borderRadius: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                                        }}
                                    >
                                        Tank Level | {pumpStatusData['Tank_Level'] || '0'}
                                    </div>
                                </div>

                                {/* Refresh Icon */}
                                <img
                                    title="Refresh"
                                    src={refreshIcon}
                                    alt="Refresh"
                                    onClick={handleRefresh}
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        cursor: 'pointer',
                                        transition: 'transform 0.3s ease-in-out',
                                        marginLeft: '20px',
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.transform = 'rotate(180deg)')}
                                    onMouseLeave={(e) => (e.currentTarget.style.transform = 'rotate(0deg)')}
                                />
                            </div>

                            {/* Lamp and Reset Section */}
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '100%',
                                    padding: '20px',
                                }}
                            >
                                {/* Lamp */}
                                <LampSVGReset state={resetLampState} />

                                {/* Reset Button */}
                                <ResetButton
                                    variant="outlined"
                                    onClick={() => handleResetClick('vlinkReset', 1, 'vLinkResetResult')}
                                    style={{ marginTop: '20px' }}
                                >
                                    <ResetIcon />
                                    Reset
                                </ResetButton>
                            </div>
                        </CenteredCard>
                    </MainContent>
                </ContentContainer>
                <Footer />
                <ToastContainer />
            </div>
        </LocalizationProvider>
    );
};

export default RemoteAccessReset;
